// ============================================================================
// AXIOS INTERCEPTORS
// ============================================================================
// Request and Response interceptors for axios instance

import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiConfig, tokenUtils, HTTP_STATUS } from '../config/apiConfig';
import { logRequest, logResponse, logError, logWarning } from './logger';
import { clearAllAuthStorage, broadcastLogout } from '../../utils/authGuard';

/**
 * Force a session-expired logout: wipe every storage key, notify sibling
 * tabs, and reload the root (`/`) so back cannot return here. With storage
 * cleared the root gate renders the login view.
 */
const forceLogoutAndRedirect = (): void => {
  broadcastLogout();
  clearAllAuthStorage();
  window.location.replace('/');
};

/**
 * Setup Request Interceptor
 * - Adds authentication token to headers
 * - Adds request timestamp for performance tracking
 * - Logs request details
 */
export const setupRequestInterceptor = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Add timestamp for performance tracking
      config.headers['X-Request-Start-Time'] = Date.now().toString();

      // Skip auth headers for authentication endpoints (discover, authenticate, refresh)
      // Sending a stale token causes JWT middleware to fetch OIDC metadata and potentially hang
      const isAuthEndpoint = config.url && (
        config.url.includes('/auth/discover') ||
        config.url.includes('/auth/authenticate') ||
        config.url.includes('/auth/refresh') ||
        config.url.includes('/auth/login')
      );

      // Always send X-App-ID (needed by discover/authenticate for clientId)
      // Env var takes priority — localStorage may have a stale value from a previous session
      const appId = process.env.REACT_APP_APPLICATION_ID
        || localStorage.getItem('application_id')
        || '';
      if (appId) {
        config.headers['X-App-ID'] = appId;
      }

      if (!isAuthEndpoint) {
        // Add authentication token if available
        const token = tokenUtils.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add tenant context headers required by the API middleware
        const tenantId = localStorage.getItem('tenant_id');
        if (tenantId) {
          config.headers['X-Tenant-ID'] = tenantId;
        }

        // Add entity and business unit context headers for branch-level scoping
        const entityId = localStorage.getItem('entity_id');
        if (entityId) {
          config.headers['X-Entity-ID'] = entityId;
        }

        const businessUnitId = localStorage.getItem('business_unit_id');
        if (businessUnitId) {
          config.headers['X-Business-Unit-ID'] = businessUnitId;
        }
      }

      // Add common headers
      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      config.headers['Accept'] = 'application/json';

      // Log request
      logRequest(config);

      return config;
    },
    (error: AxiosError) => {
      logError(error);
      return Promise.reject(error);
    }
  );
};

/**
 * Setup Response Interceptor
 * - Logs response details
 * - Handles successful responses
 * - Processes response data
 */
export const setupResponseInterceptor = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Log response
      logResponse(response);

      // Return response data directly for easier consumption
      return response;
    },
    async (error: AxiosError) => {
      // Log error
      logError(error);

      // Handle specific error cases
      if (error.response) {
        const { status } = error.response;

        // Skip automatic 401 handling for auth endpoints — let the caller handle it
        const url = error.config?.url || '';
        const isAuthEndpoint = url.includes('/auth/discover')
          || url.includes('/auth/authenticate')
          || url.includes('/auth/refresh')
          || url.includes('/auth/login');

        // Handle 401 Unauthorized - Token expired or invalid
        if (status === HTTP_STATUS.UNAUTHORIZED && !isAuthEndpoint) {
          await handleUnauthorizedError(axiosInstance, error);
        }

        // Handle 403 Forbidden - Insufficient permissions
        if (status === HTTP_STATUS.FORBIDDEN) {
          handleForbiddenError();
        }

        // Handle 404 Not Found
        if (status === HTTP_STATUS.NOT_FOUND) {
          handleNotFoundError(error);
        }

        // Handle 500 Internal Server Error
        if (status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
          handleServerError();
        }
      } else if (error.request) {
        // Request was made but no response received (network error)
        handleNetworkError();
      } else {
        // Something else happened while setting up the request
        handleUnknownError(error);
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Handle 401 Unauthorized Error
 * - Attempts to refresh token
 * - If refresh fails, redirects to login
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

async function handleUnauthorizedError(
  axiosInstance: AxiosInstance,
  error: AxiosError
): Promise<any> {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  // Avoid infinite loops
  if (originalRequest._retry) {
    logWarning('Token refresh failed, redirecting to login');
    forceLogoutAndRedirect();
    return;
  }

  // If already refreshing, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      })
      .catch(err => {
        return Promise.reject(err);
      });
  }

  originalRequest._retry = true;
  isRefreshing = true;

  const refreshToken = tokenUtils.getRefreshToken();

  if (!refreshToken) {
    logWarning('No refresh token available, redirecting to login');
    forceLogoutAndRedirect();
    return;
  }

  try {
    // Attempt to refresh token
    const response = await axiosInstance.post('/auth/refresh', {
      refreshToken,
    });

    const { token: newToken, refreshToken: newRefreshToken } = response.data;

    // Store new tokens
    tokenUtils.setToken(newToken);
    if (newRefreshToken) {
      tokenUtils.setRefreshToken(newRefreshToken);
    }

    // Update authorization header
    originalRequest.headers.Authorization = `Bearer ${newToken}`;

    // Process queued requests
    processQueue(null, newToken);

    // Retry original request
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    // Refresh failed, clear tokens and redirect to login
    processQueue(refreshError, null);
    forceLogoutAndRedirect();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
}

/**
 * Handle 403 Forbidden Error
 */
function handleForbiddenError(): void {
  logWarning('Access forbidden - Insufficient permissions');
  // Optional: Show notification to user
  // notificationService.error('You do not have permission to perform this action');
}

/**
 * Handle 404 Not Found Error
 */
function handleNotFoundError(error: AxiosError): void {
  logWarning('Resource not found', error.config?.url);
  // Optional: Show notification to user
  // notificationService.warning('Requested resource not found');
}

/**
 * Handle 500 Internal Server Error
 */
function handleServerError(): void {
  logWarning('Internal server error');
  // Optional: Show notification to user
  // notificationService.error('Server error. Please try again later');
}

/**
 * Handle Network Error
 */
function handleNetworkError(): void {
  logWarning('Network error - No response from server');
  // Optional: Show notification to user
  // notificationService.error('Network error. Please check your connection');
}

/**
 * Handle Unknown Error
 */
function handleUnknownError(error: AxiosError): void {
  logWarning('Unknown error occurred', error.message);
  // Optional: Show notification to user
  // notificationService.error('An unexpected error occurred');
}
